import { MobileDatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { Label, Textarea, TextInput } from 'flowbite-react';
import React, { useState } from 'react';
import Select from 'react-select';
import {
	backLogsOptions,
	boards,
	genderOptions,
	highestQualifications,
	postgraduateDegrees,
	stateCityOptions,
	undergraduateDegrees,
} from 'shared-utils';
import { HighestQualificationTypes } from 'src/apis/user';
import { PostgraduateDegreeTypes, PreInterviewsFormData, StateCityTypes, UndergraduateDegreeTypes } from 'types';
import UploadDocument from './UploadDocument';
import { useUserContext } from 'src/context/UserProvider';
import { pifChangableFieldsConfig } from 'src/utils/pifConfig';

const PersonalInfoForm = ({
	formData,
	setFormData,
}: {
	formData: Partial<PreInterviewsFormData>;
	setFormData: (arg: Partial<PreInterviewsFormData>) => void;
}) => {
	const { user } = useUserContext();
	const currentState = formData?.pifMeta?.location?.state as StateCityTypes;
	const currentCityOptions = stateCityOptions[currentState] || [];

	const showCityInput = formData?.pifMeta?.location?.state;

	const showTenthPercentage = formData?.pifMeta?.highestQualifications;
	const showTenthBoard = formData?.pifMeta?.highestQualifications;

	const showTwelfthPercentage = [
		HighestQualificationTypes.Twelfth,
		HighestQualificationTypes.UnderGraduate,
		HighestQualificationTypes.PostGraduate,
		HighestQualificationTypes.DiplomaAfterTwelfth,
	].includes(formData?.pifMeta?.highestQualifications);
	const showTwelfthBoard = [
		HighestQualificationTypes.Twelfth,
		HighestQualificationTypes.UnderGraduate,
		HighestQualificationTypes.PostGraduate,
		HighestQualificationTypes.DiplomaAfterTwelfth,
	].includes(formData?.pifMeta?.highestQualifications);

	const showTenthDiplomaPercentage = [HighestQualificationTypes.DiplomaAfterTenth].includes(
		formData?.pifMeta?.highestQualifications,
	);
	const showTenthDiplomaBoard = [HighestQualificationTypes.DiplomaAfterTenth].includes(
		formData?.pifMeta?.highestQualifications,
	);
	const showTenthDiplomaCollegeName = [HighestQualificationTypes.DiplomaAfterTenth].includes(
		formData?.pifMeta?.highestQualifications,
	);

	const showTwelfthDiplomaPercentage = [HighestQualificationTypes.DiplomaAfterTwelfth].includes(
		formData?.pifMeta?.highestQualifications,
	);
	const showTwelfthDiplomaBoard = [HighestQualificationTypes.DiplomaAfterTwelfth].includes(
		formData?.pifMeta?.highestQualifications,
	);
	const showTwelfthDiplomaCollegeName = [HighestQualificationTypes.DiplomaAfterTwelfth].includes(
		formData?.pifMeta?.highestQualifications,
	);

	const showUgPercentage = [HighestQualificationTypes.UnderGraduate, HighestQualificationTypes.PostGraduate].includes(
		formData?.pifMeta?.highestQualifications,
	);
	const showUgCollegeName = [HighestQualificationTypes.UnderGraduate, HighestQualificationTypes.PostGraduate].includes(
		formData?.pifMeta?.highestQualifications,
	);
	const showUgBacklog = [HighestQualificationTypes.UnderGraduate, HighestQualificationTypes.PostGraduate].includes(
		formData?.pifMeta?.highestQualifications,
	);
	const showUgDegree = [HighestQualificationTypes.UnderGraduate, HighestQualificationTypes.PostGraduate].includes(
		formData?.pifMeta?.highestQualifications,
	);
	const showUgSpecialisation =
		[HighestQualificationTypes.UnderGraduate, HighestQualificationTypes.PostGraduate].includes(
			formData?.pifMeta?.highestQualifications,
		) &&
		formData?.pifMeta?.underGraduate?.degree &&
		formData?.pifMeta?.underGraduate?.degree !== UndergraduateDegreeTypes.Others;

	const showPgPercentage = [HighestQualificationTypes.PostGraduate].includes(formData?.pifMeta?.highestQualifications);
	const showPgCollegeName = [HighestQualificationTypes.PostGraduate].includes(formData?.pifMeta?.highestQualifications);
	const showPgBacklog = [HighestQualificationTypes.PostGraduate].includes(formData?.pifMeta?.highestQualifications);
	const showPgDegree = [HighestQualificationTypes.PostGraduate].includes(formData?.pifMeta?.highestQualifications);
	const showPgSpecialisation =
		[HighestQualificationTypes.PostGraduate].includes(formData?.pifMeta?.highestQualifications) &&
		formData?.pifMeta?.postGraduate?.degree &&
		formData?.pifMeta?.postGraduate?.degree !== PostgraduateDegreeTypes.Others;

	return (
		<form className="grid grid-cols-[500px,500px] gap-x-4 gap-y-2">
			<h2 className="col-span-2 font-[600] mt-4 -mb-2">Personal Information</h2>

			<div className="mt-2 font-inter">
				<Label className="">
					Gender <span className="text-red-600">*</span>
				</Label>

				<Select
					isDisabled={pifChangableFieldsConfig?.pifMeta?.gender === false && user?.profile?.gender ? true : false}
					menuPosition="fixed"
					menuPlacement={'auto'}
					className="single-value-select-tag"
					classNamePrefix="my-select-plans"
					closeMenuOnSelect
					options={genderOptions.map(l => ({ label: l, value: l }))}
					value={genderOptions.filter(op => formData?.pifMeta?.gender?.includes(op)).map(l => ({ label: l, value: l }))}
					onChange={value => {
						setFormData({
							...formData,
							pifMeta: {
								...formData?.pifMeta,
								gender: value?.value,
							},
						});
					}}
				/>
			</div>

			<div className="mt-2 font-inter">
				<Label className="">Internship Experience (In Months)</Label>

				<TextInput
					disabled={
						pifChangableFieldsConfig?.pifMeta?.internshipExperience === false &&
						user?.pifMeta?.internshipExperience !== undefined
							? true
							: false
					}
					type="number"
					key={formData?.pifMeta ? 'internshipExperience' : 'empty internshipExperience'}
					placeholder="Internship Experience"
					value={formData?.pifMeta?.['internshipExperience']}
					onChange={e => {
						setFormData({
							...formData,
							pifMeta: {
								...formData?.pifMeta,
								internshipExperience: e.target.value,
							},
						});
					}}
				/>
			</div>
			<div className="mt-2 font-inter">
				<Label className="">
					Total Work Experience (In Months) <span className="text-red-600">*</span>
				</Label>

				<TextInput
					disabled={
						pifChangableFieldsConfig?.pifMeta?.totalExperience === false && user?.pifMeta?.totalExperience !== undefined
							? true
							: false
					}
					type="number"
					key={formData?.pifMeta ? 'totalExperience' : 'empty totalExperience'}
					placeholder="Total Experience"
					value={formData?.pifMeta?.['totalExperience']}
					onChange={e => {
						setFormData({
							...formData,
							pifMeta: {
								...formData?.pifMeta,
								totalExperience: e.target.value,
							},
						});
					}}
				/>
			</div>
			<div className="mt-2 font-inter">
				<Label className="">
					Relevant Tech Experience (In Months) <span className="text-red-600">*</span>
				</Label>

				<TextInput
					disabled={
						pifChangableFieldsConfig?.pifMeta?.relevantTechExperience === false &&
						user?.pifMeta?.relevantTechExperience !== undefined
							? true
							: false
					}
					type="number"
					key={formData?.pifMeta ? 'relevantTechExperience' : 'empty relevantTechExperience'}
					placeholder="Relevant Tech Experience"
					value={formData?.pifMeta?.['relevantTechExperience']}
					onChange={e => {
						setFormData({
							...formData,
							pifMeta: {
								...formData?.pifMeta,
								relevantTechExperience: e.target.value,
							},
						});
					}}
				/>
			</div>

			<div className="mt-2 font-inter">
				<Label className="">
					Date Of Brith <span className="text-red-600">*</span>
				</Label>

				<MobileDatePicker
					disabled={
						pifChangableFieldsConfig?.pifMeta?.dateOfBirth === false && user?.pifMeta?.dateOfBirth !== undefined
							? true
							: false
					}
					value={formData?.pifMeta?.dateOfBirth ? dayjs(formData?.pifMeta?.dateOfBirth) : null}
					format="DD MMM, YYYY"
					maxDate={dayjs()}
					onChange={date => {
						const dateValue = date as { $d?: Date };
						setFormData({
							...formData,
							pifMeta: {
								...formData?.pifMeta,
								dateOfBirth: dateValue?.$d || new Date(),
							},
						});
					}}
					sx={{
						'&': {
							width: '100%',
						},
						'& .MuiInputBase-input': {
							padding: '10px',
							borderRadius: '8px',
							backgroundColor: 'inherit',
							border: '1px solid rgb(209 213 219 / 1)',
							fontSize: '14px',
						},
						'& .MuiInputBase-root': {
							outline: 'none',
							padding: '0px',
							'&:hover': {
								outline: 'none',
							},
						},
						'& fieldset': {
							display: 'none',
						},
					}}
				/>
			</div>

			<h2 className="col-span-2 font-[600] mt-4 -mb-2">Current Location</h2>

			<div className="mt-2 font-inter">
				<Label className="">
					State <span className="text-red-600">*</span>
				</Label>

				<Select
					isDisabled={
						pifChangableFieldsConfig?.pifMeta?.location?.state === false && user?.pifMeta?.location?.state !== undefined
							? true
							: false
					}
					menuPosition="fixed"
					menuPlacement={'auto'}
					className="single-value-select-tag"
					classNamePrefix="my-select-plans"
					closeMenuOnSelect
					options={Object.keys(stateCityOptions).map(l => ({ label: l, value: l }))}
					value={Object.keys(stateCityOptions)
						.filter(op => formData?.pifMeta?.location?.state?.includes(op))
						.map(l => ({ label: l, value: l }))}
					onChange={value => {
						setFormData({
							...formData,
							pifMeta: {
								...formData?.pifMeta,
								location: {
									...formData?.pifMeta?.['location'],
									state: value?.value,
									city: '',
								},
							},
						});
					}}
				/>
			</div>

			{showCityInput ? (
				<div className="mt-2 font-inter">
					<Label className="">
						City <span className="text-red-600">*</span>
					</Label>

					<Select
						isDisabled={
							pifChangableFieldsConfig?.pifMeta?.location?.city === false && user?.pifMeta?.location?.city !== undefined
								? true
								: false
						}
						menuPosition="fixed"
						menuPlacement={'auto'}
						className="single-value-select-tag"
						classNamePrefix="my-select-plans"
						closeMenuOnSelect
						filterOption={(option: { label: string; value: string }, inputValue: string) => {
							const labelMatches = option.label.toLowerCase().includes(inputValue.toLowerCase());
							if (labelMatches) {
								return true;
							}

							const inputNotFound = !currentCityOptions.some(city =>
								city.toLowerCase().includes(inputValue.toLowerCase()),
							);
							if (inputNotFound) {
								return option.label === 'Others';
							}

							return false;
						}}
						options={stateCityOptions[formData?.pifMeta?.location?.state as StateCityTypes].map(l => ({
							label: l,
							value: l,
						}))}
						value={stateCityOptions[formData?.pifMeta?.location?.state as StateCityTypes]
							.filter(op => formData?.pifMeta?.location?.city?.includes(op))
							.map(l => ({ label: l, value: l }))}
						onChange={value => {
							setFormData({
								...formData,
								pifMeta: {
									...formData?.pifMeta,
									location: {
										...formData?.pifMeta?.['location'],
										city: value?.value,
									},
								},
							});
						}}
					/>
				</div>
			) : null}

			<div className="mt-2 font-inter">
				<Label className="">
					Pincode <span className="text-red-600">*</span>
				</Label>

				<TextInput
					disabled={
						pifChangableFieldsConfig?.pifMeta?.location?.pincode === false &&
						user?.pifMeta?.location?.pincode !== undefined
							? true
							: false
					}
					type="number"
					placeholder="Pincode"
					key={formData?.pifMeta ? 'pincode' : 'empty pincode'}
					defaultValue={formData?.pifMeta?.['location']?.pincode}
					value={formData?.pifMeta?.['location']?.pincode}
					onChange={e => {
						setFormData({
							...formData,
							pifMeta: {
								...formData?.pifMeta,
								location: {
									...formData?.pifMeta?.['location'],
									pincode: e?.target?.value,
								},
							},
						});
					}}
				/>
			</div>

			<h2 className="col-span-2 font-[600] mt-4 -mb-2">Education Details</h2>

			<div className="mt-2 font-inter">
				<Label className="">
					Highest Qualifications <span className="text-red-600">*</span>
				</Label>
				<Select
					isDisabled={
						pifChangableFieldsConfig?.pifMeta?.highestQualifications === false &&
						user?.pifMeta?.highestQualifications !== undefined
							? true
							: false
					}
					menuPosition="fixed"
					menuPlacement={'auto'}
					className="single-value-select-tag"
					classNamePrefix="my-select-plans"
					closeMenuOnSelect
					options={highestQualifications}
					value={
						formData?.pifMeta?.highestQualifications
							? highestQualifications?.find(item => item.value === formData?.pifMeta?.highestQualifications)
							: null
					}
					onChange={value => {
						setFormData({
							...formData,
							pifMeta: {
								...formData?.pifMeta,
								highestQualifications: value?.value,
							},
						});
					}}
				/>
			</div>

			{showTenthPercentage ? (
				<div className="mt-2 font-inter">
					<Label className="">
						10th Class Percentage <span className="text-red-600">*</span>
					</Label>
					<TextInput
						disabled={
							pifChangableFieldsConfig?.pifMeta?.tenth?.percentage === false &&
							user?.pifMeta?.tenth?.percentage !== undefined
								? true
								: false
						}
						type="number"
						key={formData?.pifMeta?.highestQualifications}
						placeholder="10th class percentage"
						value={formData?.pifMeta?.tenth?.percentage}
						onChange={e => {
							setFormData({
								...formData,
								pifMeta: {
									...formData?.pifMeta,
									tenth: {
										...formData?.pifMeta?.['tenth'],
										percentage: e?.target?.value,
									},
								},
							});
						}}
					/>
				</div>
			) : null}

			{showTenthBoard ? (
				<div className="mt-2 font-inter">
					<Label className="">
						10th Board Name <span className="text-red-600">*</span>
					</Label>
					<Select
						isDisabled={
							pifChangableFieldsConfig?.pifMeta?.tenth?.board === false && user?.pifMeta?.tenth?.board !== undefined
								? true
								: false
						}
						menuPosition="fixed"
						key={formData?.pifMeta?.highestQualifications}
						menuPlacement={'auto'}
						className="single-value-select-tag"
						classNamePrefix="my-select-plans"
						closeMenuOnSelect
						options={boards}
						value={boards?.find(item => item?.value === formData.pifMeta?.tenth?.board)}
						onChange={value => {
							setFormData({
								...formData,
								pifMeta: {
									...formData?.pifMeta,
									tenth: {
										...formData?.pifMeta?.['tenth'],
										board: value?.value,
									},
								},
							});
						}}
					/>
				</div>
			) : null}

			{showTwelfthPercentage ? (
				<div className="mt-2 font-inter">
					<Label className="">
						12th Class Percentage <span className="text-red-600">*</span>
					</Label>
					<TextInput
						disabled={
							pifChangableFieldsConfig?.pifMeta?.twelfth?.percentage === false &&
							user?.pifMeta?.twelfth?.percentage !== undefined
								? true
								: false
						}
						type="number"
						placeholder="12th class percentage"
						key={formData?.pifMeta?.highestQualifications}
						value={formData?.pifMeta?.['twelfth']?.percentage}
						onChange={e => {
							setFormData({
								...formData,
								pifMeta: {
									...formData?.pifMeta,
									twelfth: {
										...formData?.pifMeta?.['twelfth'],
										percentage: e?.target?.value,
									},
								},
							});
						}}
					/>
				</div>
			) : null}

			{showTwelfthBoard ? (
				<div className="mt-2 font-inter">
					<Label className="">
						12th Board Name <span className="text-red-600">*</span>
					</Label>
					<Select
						isDisabled={
							pifChangableFieldsConfig?.pifMeta?.twelfth?.board === false && user?.pifMeta?.twelfth?.board !== undefined
								? true
								: false
						}
						menuPosition="fixed"
						key={formData?.pifMeta?.highestQualifications}
						menuPlacement={'auto'}
						className="single-value-select-tag"
						classNamePrefix="my-select-plans"
						closeMenuOnSelect
						options={boards}
						value={boards?.find(item => item.value === formData?.pifMeta?.twelfth?.board)}
						onChange={value => {
							setFormData({
								...formData,
								pifMeta: {
									...formData?.pifMeta,
									twelfth: {
										...formData?.pifMeta?.['twelfth'],
										board: value?.value,
									},
								},
							});
						}}
					/>
				</div>
			) : null}

			{showTenthDiplomaPercentage ? (
				<div className="mt-2 font-inter">
					<Label className="">
						Diploma Percentage <span className="text-red-600">*</span>
					</Label>
					<TextInput
						disabled={
							pifChangableFieldsConfig?.pifMeta?.diplomaAfterTenth?.percentage === false &&
							user?.pifMeta?.diplomaAfterTenth?.percentage !== undefined
								? true
								: false
						}
						type="number"
						key={formData?.pifMeta?.highestQualifications}
						placeholder="Diploma Percentage"
						value={formData?.pifMeta?.['diplomaAfterTenth']?.percentage}
						onChange={e => {
							setFormData({
								...formData,
								pifMeta: {
									...formData?.pifMeta,
									diplomaAfterTenth: {
										...formData?.pifMeta?.['diplomaAfterTenth'],
										percentage: e?.target?.value,
									},
								},
							});
						}}
					/>
				</div>
			) : null}

			{showTenthDiplomaBoard ? (
				<div className="mt-2 font-inter">
					<Label className="">
						Diploma Board Name <span className="text-red-600">*</span>
					</Label>
					<Select
						isDisabled={
							pifChangableFieldsConfig?.pifMeta?.diplomaAfterTenth?.board === false &&
							user?.pifMeta?.diplomaAfterTenth?.board !== undefined
								? true
								: false
						}
						key={formData?.pifMeta?.highestQualifications}
						menuPosition="fixed"
						menuPlacement={'auto'}
						className="single-value-select-tag"
						classNamePrefix="my-select-plans"
						closeMenuOnSelect
						options={boards}
						value={boards?.find(item => item.value === formData?.pifMeta?.diplomaAfterTenth?.board)}
						onChange={value => {
							setFormData({
								...formData,
								pifMeta: {
									...formData?.pifMeta,
									diplomaAfterTenth: {
										...formData?.pifMeta?.['diplomaAfterTenth'],
										board: value?.value,
									},
								},
							});
						}}
					/>
				</div>
			) : null}

			{showTenthDiplomaCollegeName ? (
				<div className="mt-2 font-inter">
					<Label className="">
						Diploma College/University Name <span className="text-red-600">*</span>
					</Label>
					<TextInput
						disabled={
							pifChangableFieldsConfig?.pifMeta?.diplomaAfterTenth?.collegeUniversityName === false &&
							user?.pifMeta?.diplomaAfterTenth?.collegeUniversityName !== undefined
								? true
								: false
						}
						key={formData?.pifMeta?.highestQualifications}
						placeholder="College/University Name"
						value={formData?.pifMeta?.['diplomaAfterTenth']?.collegeUniversityName}
						onChange={e => {
							setFormData({
								...formData,
								pifMeta: {
									...formData?.pifMeta,
									diplomaAfterTenth: {
										...formData?.pifMeta?.['diplomaAfterTenth'],
										collegeUniversityName: e?.target?.value,
									},
								},
							});
						}}
					/>
				</div>
			) : null}

			{showTwelfthDiplomaPercentage ? (
				<div className="mt-2 font-inter">
					<Label className="">
						Diploma Percentage <span className="text-red-600">*</span>
					</Label>
					<TextInput
						disabled={
							pifChangableFieldsConfig?.pifMeta?.diplomaAfterTwelfth?.percentage === false &&
							user?.pifMeta?.diplomaAfterTwelfth?.percentage !== undefined
								? true
								: false
						}
						type="number"
						key={formData?.pifMeta?.highestQualifications}
						placeholder="Diploma Percentage"
						value={formData?.pifMeta?.['diplomaAfterTwelfth']?.percentage}
						onChange={e => {
							setFormData({
								...formData,
								pifMeta: {
									...formData?.pifMeta,
									diplomaAfterTwelfth: {
										...formData?.pifMeta?.['diplomaAfterTwelfth'],
										percentage: e?.target?.value,
									},
								},
							});
						}}
					/>
				</div>
			) : null}

			{showTwelfthDiplomaBoard ? (
				<div className="mt-2 font-inter">
					<Label className="">
						Diploma Board Name <span className="text-red-600">*</span>
					</Label>
					<Select
						isDisabled={
							pifChangableFieldsConfig?.pifMeta?.diplomaAfterTwelfth?.board === false &&
							user?.pifMeta?.diplomaAfterTwelfth?.board !== undefined
								? true
								: false
						}
						menuPosition="fixed"
						menuPlacement={'auto'}
						key={formData?.pifMeta?.highestQualifications}
						className="single-value-select-tag"
						classNamePrefix="my-select-plans"
						closeMenuOnSelect
						options={boards}
						value={boards?.find(item => item.value === formData?.pifMeta?.diplomaAfterTwelfth?.board)}
						onChange={value => {
							setFormData({
								...formData,
								pifMeta: {
									...formData?.pifMeta,
									diplomaAfterTwelfth: {
										...formData?.pifMeta?.['diplomaAfterTwelfth'],
										board: value?.value,
									},
								},
							});
						}}
					/>
				</div>
			) : null}

			{showTwelfthDiplomaCollegeName ? (
				<div className="mt-2 font-inter">
					<Label className="">
						Diploma College/University Name <span className="text-red-600">*</span>
					</Label>
					<TextInput
						disabled={
							pifChangableFieldsConfig?.pifMeta?.diplomaAfterTwelfth?.collegeUniversityName === false &&
							user?.pifMeta?.diplomaAfterTwelfth?.collegeUniversityName !== undefined
								? true
								: false
						}
						placeholder="College/University Name"
						key={formData?.pifMeta?.highestQualifications}
						value={formData?.pifMeta?.['diplomaAfterTwelfth']?.collegeUniversityName}
						onChange={e => {
							setFormData({
								...formData,
								pifMeta: {
									...formData?.pifMeta,
									diplomaAfterTwelfth: {
										...formData?.pifMeta?.['diplomaAfterTwelfth'],
										collegeUniversityName: e.target.value,
									},
								},
							});
						}}
					/>
				</div>
			) : null}

			{showUgPercentage ? (
				<div className="mt-2 font-inter">
					<Label className="">
						Under Graduation Percentage<span className="text-red-600">*</span>
					</Label>
					<TextInput
						disabled={
							pifChangableFieldsConfig?.pifMeta?.underGraduate?.percentage === false &&
							user?.pifMeta?.underGraduate?.percentage !== undefined
								? true
								: false
						}
						type="number"
						key={formData?.pifMeta?.highestQualifications}
						placeholder="Under Graduation Percentage"
						value={formData?.pifMeta?.underGraduate?.percentage}
						onChange={e => {
							setFormData({
								...formData,
								pifMeta: {
									...formData?.pifMeta,
									underGraduate: {
										...formData?.pifMeta?.underGraduate,
										percentage: e.target.value,
									},
								},
							});
						}}
					/>
				</div>
			) : null}

			{showUgCollegeName ? (
				<div className="mt-2 font-inter">
					<Label className="">
						Under Graduation College/University Name<span className="text-red-600">*</span>
					</Label>
					<TextInput
						disabled={
							pifChangableFieldsConfig?.pifMeta?.underGraduate?.collegeUniversityName === false &&
							user?.pifMeta?.underGraduate?.collegeUniversityName !== undefined
								? true
								: false
						}
						placeholder="Under Graduation College/University Name"
						key={formData?.pifMeta?.highestQualifications}
						value={formData?.pifMeta?.underGraduate?.collegeUniversityName}
						onChange={e => {
							setFormData({
								...formData,
								pifMeta: {
									...formData?.pifMeta,
									underGraduate: {
										...formData?.pifMeta?.underGraduate,
										collegeUniversityName: e.target.value,
									},
								},
							});
						}}
					/>
				</div>
			) : null}

			{showUgBacklog ? (
				<div className="mt-2 font-inter">
					<Label className="">
						Under Graduation Backlog<span className="text-red-600">*</span>
					</Label>
					<Select
						isDisabled={
							pifChangableFieldsConfig?.pifMeta?.underGraduate?.isBacklog === false &&
							user?.pifMeta?.underGraduate?.isBacklog !== undefined
								? true
								: false
						}
						menuPosition="fixed"
						menuPlacement={'auto'}
						key={formData?.pifMeta?.highestQualifications}
						className="single-value-select-tag"
						classNamePrefix="my-select-plans"
						closeMenuOnSelect
						options={backLogsOptions}
						value={backLogsOptions?.find(item => item.value === formData?.pifMeta?.underGraduate?.isBacklog)}
						onChange={value => {
							setFormData({
								...formData,
								pifMeta: {
									...formData?.pifMeta,
									underGraduate: {
										...formData?.pifMeta?.['underGraduate'],
										isBacklog: value?.value,
									},
								},
							});
						}}
					/>
				</div>
			) : null}

			{showUgDegree ? (
				<div className="mt-2 font-inter">
					<Label className="">
						Under Graduation Degree<span className="text-red-600">*</span>
					</Label>
					<Select
						isDisabled={
							pifChangableFieldsConfig?.pifMeta?.underGraduate?.degree === false &&
							user?.pifMeta?.underGraduate?.degree !== undefined
								? true
								: false
						}
						menuPosition="fixed"
						menuPlacement={'auto'}
						key={formData?.pifMeta?.highestQualifications}
						className="single-value-select-tag"
						classNamePrefix="my-select-plans"
						closeMenuOnSelect
						options={Object.keys(undergraduateDegrees).map(l => ({ label: l, value: l }))}
						value={Object.keys(undergraduateDegrees)
							.filter(op => formData?.pifMeta?.underGraduate?.degree?.includes(op))
							.map(l => ({ label: l, value: l }))}
						onChange={value => {
							setFormData({
								...formData,
								pifMeta: {
									...formData?.pifMeta,
									underGraduate: {
										...formData?.pifMeta?.['underGraduate'],
										degree: value?.value,
										specialisation: value?.value === UndergraduateDegreeTypes.Others ? 'Others' : null,
									},
								},
							});
						}}
					/>
				</div>
			) : null}

			{showUgSpecialisation ? (
				<div className="mt-2 font-inter">
					<Label className="">
						Under Graduation Specialisation<span className="text-red-600">*</span>
					</Label>
					<Select
						isDisabled={
							pifChangableFieldsConfig?.pifMeta?.underGraduate?.specialisation === false &&
							user?.pifMeta?.underGraduate?.specialisation !== undefined
								? true
								: false
						}
						menuPosition="fixed"
						menuPlacement={'auto'}
						key={formData?.pifMeta?.highestQualifications}
						className="single-value-select-tag"
						classNamePrefix="my-select-plans"
						closeMenuOnSelect
						options={undergraduateDegrees[formData?.pifMeta?.underGraduate?.degree as UndergraduateDegreeTypes]?.map(
							l => ({ label: l, value: l }),
						)}
						value={undergraduateDegrees[formData?.pifMeta?.underGraduate?.degree as UndergraduateDegreeTypes]
							.filter(op => formData?.pifMeta?.underGraduate?.specialisation?.includes(op))
							.map(l => ({ label: l, value: l }))}
						onChange={value => {
							setFormData({
								...formData,
								pifMeta: {
									...formData?.pifMeta,
									underGraduate: {
										...formData?.pifMeta?.['underGraduate'],
										specialisation: value?.value,
									},
								},
							});
						}}
					/>
				</div>
			) : null}

			{/* Post Grad Flow */}

			{showPgPercentage ? (
				<div className="mt-2 font-inter">
					<Label className="">
						Post Graduation Percentage<span className="text-red-600">*</span>
					</Label>
					<TextInput
						disabled={
							pifChangableFieldsConfig?.pifMeta?.postGraduate?.percentage === false &&
							user?.pifMeta?.postGraduate?.percentage !== undefined
								? true
								: false
						}
						type="number"
						placeholder="Under Graduation Percentage"
						key={formData?.pifMeta?.highestQualifications}
						value={formData?.pifMeta?.postGraduate?.percentage}
						onChange={e => {
							setFormData({
								...formData,
								pifMeta: {
									...formData?.pifMeta,
									postGraduate: {
										...formData?.pifMeta?.postGraduate,
										percentage: e.target.value,
									},
								},
							});
						}}
					/>
				</div>
			) : null}

			{showPgCollegeName ? (
				<div className="mt-2 font-inter">
					<Label className="">
						Post Graduation College/University Name<span className="text-red-600">*</span>
					</Label>
					<TextInput
						disabled={
							pifChangableFieldsConfig?.pifMeta?.postGraduate?.collegeUniversityName === false &&
							user?.pifMeta?.postGraduate?.collegeUniversityName !== undefined
								? true
								: false
						}
						placeholder="Under Graduation College/University Name"
						key={formData?.pifMeta?.highestQualifications}
						value={formData?.pifMeta?.postGraduate?.collegeUniversityName}
						onChange={e => {
							setFormData({
								...formData,
								pifMeta: {
									...formData?.pifMeta,
									postGraduate: {
										...formData?.pifMeta?.postGraduate,
										collegeUniversityName: e.target.value,
									},
								},
							});
						}}
					/>
				</div>
			) : null}

			{showPgBacklog ? (
				<div className="mt-2 font-inter">
					<Label className="">
						Post Graduation Backlog<span className="text-red-600">*</span>
					</Label>
					<Select
						isDisabled={
							pifChangableFieldsConfig?.pifMeta?.postGraduate?.isBacklog === false &&
							user?.pifMeta?.postGraduate?.isBacklog !== undefined
								? true
								: false
						}
						menuPosition="fixed"
						menuPlacement={'auto'}
						className="single-value-select-tag"
						classNamePrefix="my-select-plans"
						key={formData?.pifMeta?.highestQualifications}
						closeMenuOnSelect
						options={backLogsOptions}
						value={backLogsOptions?.find(item => item.value === formData?.pifMeta?.postGraduate?.isBacklog)}
						onChange={value => {
							setFormData({
								...formData,
								pifMeta: {
									...formData?.pifMeta,
									postGraduate: {
										...formData?.pifMeta?.['postGraduate'],
										isBacklog: value?.value,
									},
								},
							});
						}}
					/>
				</div>
			) : null}

			{showPgDegree ? (
				<div className="mt-2 font-inter">
					<Label className="">
						Post Graduation Degree<span className="text-red-600">*</span>
					</Label>
					<Select
						isDisabled={
							pifChangableFieldsConfig?.pifMeta?.postGraduate?.degree === false &&
							user?.pifMeta?.postGraduate?.degree !== undefined
								? true
								: false
						}
						menuPosition="fixed"
						menuPlacement={'auto'}
						className="single-value-select-tag"
						classNamePrefix="my-select-plans"
						key={formData?.pifMeta?.highestQualifications}
						closeMenuOnSelect
						options={Object.keys(postgraduateDegrees).map(l => ({ label: l, value: l }))}
						value={Object.keys(postgraduateDegrees)
							.filter(op => formData?.pifMeta?.postGraduate?.degree?.includes(op))
							.map(l => ({ label: l, value: l }))}
						onChange={value => {
							setFormData({
								...formData,
								pifMeta: {
									...formData?.pifMeta,
									postGraduate: {
										...formData?.pifMeta?.['postGraduate'],
										degree: value?.value,
										specialisation: value?.value === PostgraduateDegreeTypes.Others ? 'Others' : null,
									},
								},
							});
						}}
					/>
				</div>
			) : null}

			{showPgSpecialisation ? (
				<div className="mt-2 font-inter">
					<Label className="">
						Post Graduation Specialisation<span className="text-red-600">*</span>
					</Label>
					<Select
						isDisabled={
							pifChangableFieldsConfig?.pifMeta?.postGraduate?.specialisation === false &&
							user?.pifMeta?.postGraduate?.specialisation !== undefined
								? true
								: false
						}
						menuPosition="fixed"
						menuPlacement={'auto'}
						className="single-value-select-tag"
						classNamePrefix="my-select-plans"
						key={formData?.pifMeta?.highestQualifications}
						closeMenuOnSelect
						options={postgraduateDegrees[formData?.pifMeta?.postGraduate?.degree as PostgraduateDegreeTypes]?.map(
							l => ({ label: l, value: l }),
						)}
						value={postgraduateDegrees[formData?.pifMeta?.postGraduate?.degree as PostgraduateDegreeTypes]
							.filter(op => formData?.pifMeta?.postGraduate?.specialisation?.includes(op))
							.map(l => ({ label: l, value: l }))}
						onChange={value => {
							setFormData({
								...formData,
								pifMeta: {
									...formData?.pifMeta,
									postGraduate: {
										...formData?.pifMeta?.['postGraduate'],
										specialisation: value?.value,
									},
								},
							});
						}}
					/>
				</div>
			) : null}
			{true && (
				<>
					<h2 className="col-span-2 font-[600] mt-4 -mb-2">Upload photo/documents</h2>
					{/* Upload User Photo userPassportPhoto */}
					{/* <div className="col-span-2 mt-2 font-inter">
						<UploadDocument
							uploadedUrl={formData?.pifMeta?.userPassportPhoto}
							acceptedFormatText=".png, .jpeg are allowed"
							setUploadedUrl={url => {
								setFormData({
									...formData,
									pifMeta: {
										...formData?.pifMeta,
										userPassportPhoto: url,
									},
								});
							}}
							headingText="Upload your passport size photo"
						/>
					</div> */}
					{/* 10th marksheet tenthMarksheetDoc */}
					<div className="col-span-2 mt-2 font-inter">
						<UploadDocument
							isDisabled={
								pifChangableFieldsConfig?.pifMeta?.tenthMarksheetDoc === false &&
								user?.pifMeta?.tenthMarksheetDoc !== undefined
									? true
									: false
							}
							acceptedFormats="image/png, image/jpeg ,application/pdf ,application/zip"
							acceptedFormatText=".png, .jpeg, .pdf, .zip files are allowed"
							uploadedUrl={formData?.pifMeta?.tenthMarksheetDoc}
							setUploadedUrl={url => {
								setFormData({
									...formData,
									pifMeta: {
										...formData?.pifMeta,
										tenthMarksheetDoc: url,
									},
								});
							}}
							headingText="Upload your 10th marksheet"
						/>
					</div>
					{/* 12th  Marksheet twelfthMarksheetDoc */}
					<div className="col-span-2 mt-2 font-inter">
						<UploadDocument
							isDisabled={
								pifChangableFieldsConfig?.pifMeta?.twelfthMarksheetDoc === false &&
								user?.pifMeta?.twelfthMarksheetDoc !== undefined
									? true
									: false
							}
							acceptedFormats="image/png, image/jpeg ,application/pdf ,application/zip"
							acceptedFormatText=".png, .jpeg, .pdf, .zip files are allowed"
							uploadedUrl={formData?.pifMeta?.twelfthMarksheetDoc}
							setUploadedUrl={url => {
								setFormData({
									...formData,
									pifMeta: {
										...formData?.pifMeta,
										twelfthMarksheetDoc: url,
									},
								});
							}}
							headingText="Upload your 12th marksheet"
						/>
					</div>
					{/* Highest Qualification Marksheet highestQualificationMarksheetDoc */}
					<div className="col-span-2 mt-2 font-inter">
						<UploadDocument
							isDisabled={
								pifChangableFieldsConfig?.pifMeta?.highestQualificationMarksheetDoc === false &&
								user?.pifMeta?.highestQualificationMarksheetDoc !== undefined
									? true
									: false
							}
							acceptedFormats="image/png, image/jpeg ,application/pdf ,application/zip"
							acceptedFormatText=".png, .jpeg, .pdf, .zip files are allowed"
							uploadedUrl={formData?.pifMeta?.highestQualificationMarksheetDoc}
							setUploadedUrl={url => {
								setFormData({
									...formData,
									pifMeta: {
										...formData?.pifMeta,
										highestQualificationMarksheetDoc: url,
									},
								});
							}}
							headingText="Upload your highest qualification marksheet"
						/>
					</div>
					{/* PAN panCardDoc */}
					<div className="col-span-2 mt-2 font-inter">
						<UploadDocument
							isDisabled={
								pifChangableFieldsConfig?.pifMeta?.panCardDoc === false && user?.pifMeta?.panCardDoc !== undefined
									? true
									: false
							}
							isPublic={false}
							acceptedFormats="image/png, image/jpeg ,application/pdf ,application/zip"
							acceptedFormatText=".png, .jpeg, .pdf, .zip files are allowed"
							uploadedUrl={formData?.pifMeta?.panCardDoc}
							setUploadedUrl={url => {
								setFormData({
									...formData,
									pifMeta: {
										...formData?.pifMeta,
										panCardDoc: url,
									},
								});
							}}
							headingText="Upload your PAN card"
						/>
					</div>
					{/* Aadhaar aadhaarCardDocFront */}
					<div className="col-span-2 mt-2 font-inter">
						<UploadDocument
							isDisabled={
								pifChangableFieldsConfig?.pifMeta?.aadhaarCardDocFront === false &&
								user?.pifMeta?.aadhaarCardDocFront !== undefined
									? true
									: false
							}
							isPublic={false}
							acceptedFormats="image/png, image/jpeg ,application/pdf ,application/zip"
							acceptedFormatText=".png, .jpeg, .pdf, .zip files are allowed"
							uploadedUrl={formData?.pifMeta?.aadhaarCardDocFront}
							setUploadedUrl={url => {
								setFormData({
									...formData,
									pifMeta: {
										...formData?.pifMeta,
										aadhaarCardDocFront: url,
									},
								});
							}}
							headingText="Upload your Aadhaar card (front side)"
						/>
					</div>
					{/* Aadhaar aadhaarCardDocBack */}
					<div className="col-span-2 mt-2 font-inter">
						<UploadDocument
							isDisabled={
								pifChangableFieldsConfig?.pifMeta?.aadhaarCardDocBack === false &&
								user?.pifMeta?.aadhaarCardDocBack !== undefined
									? true
									: false
							}
							isPublic={false}
							acceptedFormats="image/png, image/jpeg ,application/pdf ,application/zip"
							acceptedFormatText=".png, .jpeg, .pdf, .zip files are allowed"
							uploadedUrl={formData?.pifMeta?.aadhaarCardDocBack}
							setUploadedUrl={url => {
								setFormData({
									...formData,
									pifMeta: {
										...formData?.pifMeta,
										aadhaarCardDocBack: url,
									},
								});
							}}
							headingText="Upload your Aadhaar card (back side)"
						/>
					</div>

					{user?.flags?.isMasaiStudent ? null : (
						<>
							<h2 className="col-span-2 font-[600] mt-4 -mb-2">Social profile details</h2>

							<div className="mt-2  font-inter">
								<Label className="">
									Linkedin Url <span className="text-red-600">*</span>
								</Label>

								<TextInput
									type="text"
									placeholder="Linkedin Url"
									value={formData?.pifMeta?.linkedinUrl}
									onChange={e => {
										setFormData({
											...formData,
											pifMeta: {
												...formData?.pifMeta,
												linkedinUrl: e?.target?.value,
											},
										});
									}}
								/>
							</div>
							<div className="mt-2  font-inter">
								<Label className="">
									Github Profile link <span className="text-red-600">*</span>
								</Label>

								<TextInput
									type="text"
									placeholder="Leetcode Profile Link "
									value={formData?.pifMeta?.githubProfileLink}
									onChange={e => {
										setFormData({
											...formData,
											pifMeta: {
												...formData?.pifMeta,
												githubProfileLink: e?.target?.value,
											},
										});
									}}
								/>
							</div>
							<div className="mt-2 font-inter">
								<Label className="">
									Leetcode Profile Link (write NA if not applicable) <span className="text-red-600">*</span>
								</Label>

								<TextInput
									type="text"
									placeholder="Leetcode Profile Link"
									value={formData?.pifMeta?.leetcodeProfileLink}
									onChange={e => {
										setFormData({
											...formData,
											pifMeta: {
												...formData?.pifMeta,
												leetcodeProfileLink: e?.target?.value,
											},
										});
									}}
								/>
							</div>

							<div className="mt-2  font-inter">
								<Label className="">
									Hacker Rank Profile Link (write NA if not applicable) <span className="text-red-600">*</span>
								</Label>

								<TextInput
									type="text"
									placeholder="Leetcode Profile Link "
									value={formData?.pifMeta?.hackerRankProfileLink}
									onChange={e => {
										setFormData({
											...formData,
											pifMeta: {
												...formData?.pifMeta,
												hackerRankProfileLink: e?.target?.value,
											},
										});
									}}
								/>
							</div>
							<div className="mt-2  font-inter">
								<Label className="">
									Facebook Profile link (write NA if not applicable) <span className="text-red-600">*</span>
								</Label>

								<TextInput
									type="text"
									placeholder="Leetcode Profile Link "
									value={formData?.pifMeta?.facebookProfileLink}
									onChange={e => {
										setFormData({
											...formData,
											pifMeta: {
												...formData?.pifMeta,
												facebookProfileLink: e?.target?.value,
											},
										});
									}}
								/>
							</div>
							<div className="mt-2  font-inter">
								<Label className="">
									Instagram Profile link (write NA if not applicable) <span className="text-red-600">*</span>
								</Label>

								<TextInput
									type="text"
									placeholder="Leetcode Profile Link "
									value={formData?.pifMeta?.instagramProfileLink}
									onChange={e => {
										setFormData({
											...formData,
											pifMeta: {
												...formData?.pifMeta,
												instagramProfileLink: e?.target?.value,
											},
										});
									}}
								/>
							</div>
						</>
					)}
					<h2 className="col-span-2 font-[600] mt-4 -mb-2">Contact Information</h2>
					{/* Parent Mobile Number guardianMobileNumber  */}
					<div className="mt-2 font-inter">
						<Label className="">
							Parent/Guardian Phone Number <span className="text-red-600">*</span>
						</Label>

						<TextInput
							disabled={
								pifChangableFieldsConfig?.pifMeta?.guardianMobileNumber === false &&
								user?.pifMeta?.guardianMobileNumber !== undefined
									? true
									: false
							}
							type="text"
							placeholder="Phone Number"
							value={formData?.pifMeta?.guardianMobileNumber}
							onChange={e => {
								setFormData({
									...formData,
									pifMeta: {
										...formData?.pifMeta,
										guardianMobileNumber: e?.target?.value,
									},
								});
							}}
						/>
					</div>

					{/* primary communication phone number  */}
					<div className="mt-2 font-inter">
						<Label className="">
							Primary communication phone number <span className="text-red-600">*</span>
						</Label>

						<TextInput
							disabled={
								pifChangableFieldsConfig?.pifMeta?.primaryCommunicationPhoneNumber === false &&
								user?.pifMeta?.primaryCommunicationPhoneNumber !== undefined
									? true
									: false
							}
							type="text"
							placeholder="Phone Number"
							value={formData?.pifMeta?.primaryCommunicationPhoneNumber}
							onChange={e => {
								setFormData({
									...formData,
									pifMeta: {
										...formData?.pifMeta,
										primaryCommunicationPhoneNumber: e?.target?.value,
									},
								});
							}}
						/>
					</div>
					{/* primary communication email  */}
					<div className="mt-2 font-inter">
						<Label className="">
							Primary communication email <span className="text-red-600">*</span>
						</Label>

						<TextInput
							disabled={
								pifChangableFieldsConfig?.pifMeta?.primaryCommunicationEmail === false &&
								user?.pifMeta?.primaryCommunicationEmail !== undefined
									? true
									: false
							}
							type="text"
							placeholder="enter email"
							value={formData?.pifMeta?.primaryCommunicationEmail}
							onChange={e => {
								setFormData({
									...formData,
									pifMeta: {
										...formData?.pifMeta,
										primaryCommunicationEmail: e?.target?.value,
									},
								});
							}}
						/>
					</div>
					{user?.flags?.isMasaiStudent ? null : (
						<>
							{' '}
							<h2 className="col-span-2 font-[600] mt-4 -mb-2">Hobbies and achievements </h2>
							<div className="mt-2  font-inter">
								<Label className="">
									Clubs involved in college (write NA if not applicable)<span className="text-red-600">*</span>
								</Label>

								<Textarea
									// style={{
									// 	fontFamily: "'Inter', sans-serif",
									// 	height: '90px',
									// }}
									placeholder="Clubs involved in college"
									value={formData?.pifMeta?.clubsInvolvedInCollege}
									onChange={e => {
										setFormData({
											...formData,
											pifMeta: {
												...formData?.pifMeta,
												clubsInvolvedInCollege: e?.target?.value,
											},
										});
									}}
								/>
							</div>
							<div className="mt-2  font-inter">
								<Label className="">
									Hobbies and interest (write NA if not applicable) <span className="text-red-600">*</span>
								</Label>

								<Textarea
									// style={{
									// 	fontFamily: "'Inter', sans-serif",
									// 	height: '90px',
									// }}
									placeholder="Hobbies and interest"
									value={formData?.pifMeta?.hobbies}
									onChange={e => {
										setFormData({
											...formData,
											pifMeta: {
												...formData?.pifMeta,
												hobbies: e?.target?.value,
											},
										});
									}}
								/>
							</div>
							<div className="mt-2  font-inter">
								<Label className="">
									Scholarships, accolades in college (write NA if not applicable)<span className="text-red-600">*</span>
								</Label>

								<Textarea
									// style={{
									// 	fontFamily: "'Inter', sans-serif",
									// 	height: '90px',
									// }}
									placeholder="Scholarships, accolades in college"
									value={formData?.pifMeta?.achievements}
									onChange={e => {
										setFormData({
											...formData,
											pifMeta: {
												...formData?.pifMeta,
												achievements: e?.target?.value,
											},
										});
									}}
								/>
							</div>
						</>
					)}
				</>
			)}
		</form>
	);
};

export default PersonalInfoForm;
